import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getStoredToken } from '../App';
import { 
  Upload, Music, Play, Pause, Trash2, Plus, Image as ImageIcon, 
  Video, Loader2, ChevronLeft, ChevronRight, Clock, Sparkles,
  Settings, Zap, Move, Crop, Timer, Maximize2, AlertTriangle, X, Eye,
  Save, FolderOpen, Volume2, VolumeX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import AIGenerateModal from '../components/AIGenerateModal';
import VideoFormatDrawer, { getFormatById } from '../components/VideoFormatDrawer';
import SongPickerDrawer from '../components/SongPickerDrawer';
import VideoTrimmer from '../components/VideoTrimmer';
import VideoSettingsPanel from '../components/VideoSettingsPanel';
import DraftsDrawer from '../components/DraftsDrawer';
import PhotoCropModal from '../components/PhotoCropModal';
import MainNav from '../components/MainNav';

const API = process.env.REACT_APP_BACKEND_URL;

// Fit mode options
const FIT_MODES = [
  { id: 'cover', label: 'Fill', desc: 'Cover entire frame, may crop edges', icon: Maximize2 },
  { id: 'contain', label: 'Fit', desc: 'Show entire photo, may have bars', icon: ImageIcon },
  { id: 'center', label: 'Center', desc: 'Center crop to fit frame', icon: Crop },
];

// Transition options
const TRANSITIONS = [
  { id: 'none', label: 'None', desc: 'Straight cuts — no transition' },
  { id: 'crossfade', label: 'Crossfade', desc: 'Smooth fade between photos' },
  { id: 'fade', label: 'Fade to Black', desc: 'Fade out then fade in' },
  { id: 'slide', label: 'Slide', desc: 'Slide in from side' },
  { id: 'zoom', label: 'Zoom', desc: 'Zoom transition' },
];

export default function CreateVideoLegacy() {
  const navigate = useNavigate();
  
  // Mode toggle
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  
  // Basic state
  const [songs, setSongs] = useState([]);
  const [selectedSong, setSelectedSong] = useState(null);
  const [photos, setPhotos] = useState([]); // Each item: { url, id, type: 'photo'|'video', duration?, fitMode?, position?, inPoint?, outPoint?, videoDuration? }
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [videoId, setVideoId] = useState(null);
  const [videoStatus, setVideoStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [showFormatDrawer, setShowFormatDrawer] = useState(false);
  const [showSongDrawer, setShowSongDrawer] = useState(false);
  
  // Quick mode settings (fixed)
  const QUICK_MODE_DURATION = 5;
  
  // Advanced mode settings
  const [globalDuration, setGlobalDuration] = useState(5);
  const [usePerSlideTiming, setUsePerSlideTiming] = useState(false);
  const [globalFitMode, setGlobalFitMode] = useState('cover');
  const [transitionType, setTransitionType] = useState('crossfade');
  const [kenBurnsEnabled, setKenBurnsEnabled] = useState(false);
  const [musicStartTime, setMusicStartTime] = useState(0);
  const [endCardEnabled, setEndCardEnabled] = useState(true);
  const [orientation, setOrientation] = useState('landscape');

  // Phase 3 — global "duck song during clip audio" toggle. Default ON; when
  // OFF and a song + audio-enabled clip are both present, mode flips from
  // 'duck' to 'both_full' (song + clip audio at 100% each).
  const [duckSongEnabled, setDuckSongEnabled] = useState(true);

  // Crop mode: 'auto' = legacy behaviour (cover + portrait→top); 'manual' =
  // each photo opens a focus-point picker (PhotoCropModal). Stored per-photo
  // as { cropPosition: { x, y }, cropZoom } and applied via CSS object-position
  // in the preview + sent to HP Server as crop_positions array on render.
  const [cropMode, setCropMode] = useState('auto');
  const [cropEditorPhotoIndex, setCropEditorPhotoIndex] = useState(null);

  // Helper: build the CSS object-position string for a photo. Uses manual
  // focus point when cropMode === 'manual' AND the user has set one. Falls
  // back to the legacy auto behaviour (top-bias for portraits).
  const objectPositionFor = (photo) => {
    if (cropMode === 'manual' && photo?.cropPosition) {
      const { x, y } = photo.cropPosition;
      return `${x}% ${y}%`;
    }
    // Auto mode: top-bias for portrait orientation, centred otherwise.
    return orientation === 'portrait' ? '50% 0%' : '50% 50%';
  };

  // Aspect ratio for the crop modal — matches the chosen output orientation.
  const cropAspect = orientation === 'portrait'
    ? 9 / 16
    : orientation === 'square' ? 1 : 16 / 9;
  
  // Selected photo for editing (advanced mode)
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  
  // Track if user visited/modified Advanced mode settings
  const [hasUsedAdvancedMode, setHasUsedAdvancedMode] = useState(false);
  
  // Check if Advanced settings differ from defaults
  const hasAdvancedChanges = useMemo(() => {
    return globalDuration !== 5 || 
           usePerSlideTiming || 
           globalFitMode !== 'cover' || 
           transitionType !== 'crossfade' || 
           kenBurnsEnabled || 
           musicStartTime > 0 ||
           !endCardEnabled ||
           photos.some(p => p.duration !== undefined || p.fitMode !== undefined);
  }, [globalDuration, usePerSlideTiming, globalFitMode, transitionType, kenBurnsEnabled, musicStartTime, endCardEnabled, photos]);
  
  // Set flag when switching to Advanced mode
  useEffect(() => {
    if (isAdvancedMode) {
      setHasUsedAdvancedMode(true);
    }
  }, [isAdvancedMode]);
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewCurrentTime, setPreviewCurrentTime] = useState(0);
  const [previewPhotoIndex, setPreviewPhotoIndex] = useState(0);
  const previewAudioRef = React.useRef(null);
  const previewIntervalRef = React.useRef(null);

  // Cancel render state
  const [cancellingRender, setCancellingRender] = useState(false);

  // Drafts state
  const [showDraftsDrawer, setShowDraftsDrawer] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null); // set after save or load; lets re-save update in place

  // Load user songs
  useEffect(() => {
    const loadSongs = async () => {
      try {
        const { data } = await apiFetch('/songs');
        const completedSongs = data.filter(s => s.status === 'completed' && s.audio_url);
        setSongs(completedSongs);
      } catch (err) {
        console.error('Failed to load songs:', err);
      }
    };
    loadSongs();
  }, []);

  // Load settings for cost
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data } = await apiFetch('/credits/costs');
        setSettings(data);
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    };
    loadSettings();
  }, []);

  // Poll for video status
  useEffect(() => {
    if (!videoId) return;
    
    let failCount = 0;
    const MAX_FAIL_POLLS = 60; // Keep checking HP server for 3 more minutes after "failed"
    
    const pollStatus = async () => {
      try {
        const { data } = await apiFetch(`/slideshow/${videoId}`);
        
        if (data.status === 'completed') {
          setVideoStatus(data);
          setGenerating(false);
          return; // Stop polling
        }
        
        if (data.status === 'failed') {
          // User-cancelled jobs finalize immediately without the retry loop
          if (data.cancelled) {
            setVideoStatus(data);
            setGenerating(false);
            return;
          }
          failCount++;
          if (failCount >= MAX_FAIL_POLLS) {
            // HP server confirmed it's really failed after 3 minutes of re-checking
            setVideoStatus(data);
            setGenerating(false);
            return;
          }
          // Don't show failed yet — keep polling, HP server might still be rendering
          setVideoStatus({ ...data, status: 'processing', progress: 'Rendering in progress...' });
          return;
        }
        
        // Still processing
        failCount = 0;
        setVideoStatus(data);
      } catch (err) {
        console.error('Failed to get video status:', err);
      }
    };
    
    const interval = setInterval(pollStatus, 3000);
    pollStatus();
    
    return () => clearInterval(interval);
  }, [videoId]);

  // Cancel the current slideshow render
  const handleCancelRender = async () => {
    if (!videoId || cancellingRender) return;
    if (!window.confirm('Cancel this render? Any credits charged will NOT be refunded automatically — contact support if needed.')) return;
    setCancellingRender(true);
    try {
      await apiFetch(`/slideshow/${videoId}/cancel`, { method: 'POST' });
      // Optimistic: immediately reflect failure state so the UI exits the spinner
      setVideoStatus(prev => prev ? { ...prev, status: 'failed', cancelled: true, error: 'Cancelled by user' } : prev);
      setGenerating(false);
    } catch (err) {
      console.error('Cancel render failed:', err);
      alert('Cancel request failed. The render may still be running on the server.');
    } finally {
      setCancellingRender(false);
    }
  };

  // Save current editor state as a draft (creates new, or updates in place
  // if a draft is currently loaded)
  const handleSaveDraft = async () => {
    if (savingDraft) return;
    if (!selectedSong && photos.length === 0) {
      alert('Nothing to save yet — pick a song or add photos first.');
      return;
    }
    const defaultName = `${selectedSong?.title || 'Draft'} — ${new Date().toLocaleDateString()}`;
    const name = window.prompt('Name this draft:', defaultName);
    if (!name || !name.trim()) return;
    setSavingDraft(true);
    try {
      const payload = {
        draft_id: currentDraftId || undefined,
        name: name.trim(),
        song_id: selectedSong?.song_id || null,
        photos: photos,
        settings: {
          isAdvancedMode,
          globalDuration,
          usePerSlideTiming,
          globalFitMode,
          transitionType,
          kenBurnsEnabled,
          musicStartTime,
          endCardEnabled,
          orientation,
        },
      };
      const { data } = await apiFetch('/slideshow-drafts', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (data?.draft_id) setCurrentDraftId(data.draft_id);
      // Lightweight confirmation
      if (typeof window !== 'undefined') {
        // Prefer a toast if sonner is available on this page
        try {
          const { toast } = await import('sonner');
          toast.success(data?.updated ? 'Draft updated' : 'Draft saved');
        } catch {
          alert(data?.updated ? 'Draft updated' : 'Draft saved');
        }
      }
    } catch (err) {
      console.error('Save draft failed:', err);
      alert('Could not save draft: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingDraft(false);
    }
  };

  // Load a draft back into the editor
  const handleLoadDraft = (draft) => {
    if (!draft) return;
    const s = draft.settings || {};
    // Find the matching song in our loaded songs (if any)
    const matchingSong = draft.song_id
      ? songs.find(sg => sg.song_id === draft.song_id) || null
      : null;
    setSelectedSong(matchingSong);
    setPhotos(Array.isArray(draft.photos) ? draft.photos : []);
    if (typeof s.isAdvancedMode === 'boolean') setIsAdvancedMode(s.isAdvancedMode);
    if (typeof s.globalDuration === 'number') setGlobalDuration(s.globalDuration);
    if (typeof s.usePerSlideTiming === 'boolean') setUsePerSlideTiming(s.usePerSlideTiming);
    if (typeof s.globalFitMode === 'string') setGlobalFitMode(s.globalFitMode);
    if (typeof s.transitionType === 'string') setTransitionType(s.transitionType);
    if (typeof s.kenBurnsEnabled === 'boolean') setKenBurnsEnabled(s.kenBurnsEnabled);
    if (typeof s.musicStartTime === 'number') setMusicStartTime(s.musicStartTime);
    if (typeof s.endCardEnabled === 'boolean') setEndCardEnabled(s.endCardEnabled);
    if (typeof s.orientation === 'string') setOrientation(s.orientation);
    setCurrentDraftId(draft.draft_id);
    // Clear any lingering render state
    setVideoId(null);
    setVideoStatus(null);
    setGenerating(false);
    setError('');
    setWarning('');
  };

  // Calculate estimated duration
  const estimatedDuration = useMemo(() => {
    if (photos.length === 0) return 0;
    
    const quickDur = QUICK_MODE_DURATION;
    const imgSlideDur = isAdvancedMode ? globalDuration : quickDur;
    
    let duration = 0;
    photos.forEach((photo) => {
      if (photo.type === 'video') {
        // Videos use their trimmed length (or full length if no trim)
        const inP = photo.inPoint ?? 0;
        const outP = photo.outPoint ?? (photo.videoDuration ?? 0);
        duration += Math.max(0, outP - inP);
      } else if (isAdvancedMode && usePerSlideTiming) {
        duration += photo.duration || globalDuration;
      } else {
        duration += imgSlideDur;
      }
    });
    
    // Add end card (black screen) if enabled
    const cardDuration = isAdvancedMode ? globalDuration : quickDur;
    if (endCardEnabled) duration += cardDuration;
    
    return duration;
  }, [photos, isAdvancedMode, usePerSlideTiming, globalDuration, endCardEnabled]);

  // Check if slides exceed song length
  useEffect(() => {
    if (selectedSong && photos.length > 0) {
      const songLength = selectedSong.duration || 180;
      if (estimatedDuration > songLength) {
        setWarning(`Your slideshow (${formatDuration(estimatedDuration)}) is longer than your song (${formatDuration(songLength)}). The song will loop or video will be cut short.`);
      } else {
        setWarning('');
      }
    } else {
      setWarning('');
    }
  }, [selectedSong, estimatedDuration, photos.length]);

  // Upload size limits — fetched from backend so they stay in sync
  const [uploadLimits, setUploadLimits] = useState({ max_photo_mb: 25, max_video_mb: 100 });
  useEffect(() => {
    fetch(`${API}/api/slideshow/upload-limits`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setUploadLimits(d); })
      .catch(() => { /* fallback defaults are fine */ });
  }, []);

  // Handle photo/video upload
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || e.dataTransfer?.files || []);
    if (files.length === 0) return;

    if (photos.length + files.length > 50) {
      setError('Maximum 50 items allowed');
      return;
    }

    // Client-side size pre-check — gives instant feedback without burning bandwidth
    const oversized = [];
    for (const file of files) {
      const isVid = file.type.startsWith('video/');
      const isImg = file.type.startsWith('image/');
      if (!isVid && !isImg) continue;
      const limitMB = isVid ? uploadLimits.max_video_mb : uploadLimits.max_photo_mb;
      const limitBytes = limitMB * 1024 * 1024;
      if (file.size > limitBytes) {
        oversized.push({
          name: file.name,
          sizeMB: (file.size / (1024 * 1024)).toFixed(1),
          limitMB,
          kind: isVid ? 'video' : 'photo',
        });
      }
    }
    if (oversized.length > 0) {
      const o = oversized[0];
      const others = oversized.length > 1 ? ` (and ${oversized.length - 1} more)` : '';
      setError(
        `${o.name} is ${o.sizeMB} MB — exceeds the ${o.limitMB} MB limit for ${o.kind}s${others}. ` +
        `Try compressing or trimming before upload.`
      );
      return;
    }

    setUploading(true);
    setError('');
    
    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) continue;
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const token = getStoredToken();
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const uploadEndpoint = isVideo 
          ? `${API}/api/slideshow/upload-video`
          : `${API}/api/slideshow/upload-photo`;
        
        const response = await fetch(uploadEndpoint, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: formData
        });

        if (!response.ok) {
          // Surface the backend's specific error message (size limit, type, server error)
          let msg = `Upload failed (${response.status})`;
          try {
            const errData = await response.json();
            if (errData.detail) msg = errData.detail;
          } catch (_) {
            // Body wasn't JSON — keep default
          }
          throw new Error(msg);
        }
        const data = await response.json();
        
        if (isVideo) {
          const videoDuration = await getVideoDuration(file);
          setPhotos(prev => [...prev, { 
            url: data.url, 
            id: data.video_id,
            type: 'video',
            videoDuration: videoDuration || 10,
            inPoint: 0,
            outPoint: videoDuration || 10,
            fitMode: globalFitMode,
            audioEnabled: true,  // Phase 2 — per-clip audio toggle (default ON)
          }]);
        } else {
          setPhotos(prev => [...prev, { 
            url: data.url, 
            id: data.photo_id,
            type: 'photo',
            duration: globalDuration,
            fitMode: globalFitMode,
            position: { x: 50, y: 50 },
            zoom: 100
          }]);
        }
      } catch (err) {
        console.error('Upload failed:', err);
        setError(`${file.name}: ${err.message || 'Upload failed'}`);
      }
    }
    
    setUploading(false);
  };
  
  // Get duration of a video file
  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(Math.round(video.duration * 10) / 10);
      };
      video.onerror = () => resolve(null);
      video.src = URL.createObjectURL(file);
    });
  };
  
  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
    handlePhotoUpload(e);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('border-primary', 'bg-primary/10');
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
  };

  // Remove photo
  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    if (selectedPhotoIndex === index) setSelectedPhotoIndex(null);
  };

  // Reorder photos
  const movePhoto = (index, direction) => {
    const newPhotos = [...photos];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      setPhotos(newPhotos);
    }
  };

  // Update individual photo settings (advanced mode)
  const updatePhotoSetting = (index, key, value) => {
    setPhotos(prev => prev.map((photo, i) => 
      i === index ? { ...photo, [key]: value } : photo
    ));
  };

  // Apply global settings to all photos
  const applyGlobalToAll = () => {
    setPhotos(prev => prev.map(photo => ({
      ...photo,
      duration: globalDuration,
      fitMode: globalFitMode
    })));
  };

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate photo timeline for preview
  const photoTimeline = useMemo(() => {
    let currentTime = 0;
    return photos.map((photo, index) => {
      let duration;
      if (photo.type === 'video') {
        // Video uses trimmed (or full) length — timing controls never apply
        const inP = photo.inPoint ?? 0;
        const outP = photo.outPoint ?? (photo.videoDuration ?? 0);
        duration = Math.max(0.5, outP - inP);
      } else if (isAdvancedMode && usePerSlideTiming) {
        duration = photo.duration || globalDuration;
      } else {
        duration = isAdvancedMode ? globalDuration : QUICK_MODE_DURATION;
      }
      const start = currentTime;
      currentTime += duration;
      return { ...photo, index, start, end: currentTime, duration };
    });
  }, [photos, globalDuration, usePerSlideTiming, isAdvancedMode, QUICK_MODE_DURATION]);
  
  // Total slideshow duration
  const totalDuration = useMemo(() => {
    return photoTimeline.reduce((sum, p) => sum + p.duration, 0);
  }, [photoTimeline]);
  
  // Get current photo based on preview time
  const getCurrentPhotoIndex = (time) => {
    for (let i = 0; i < photoTimeline.length; i++) {
      if (time >= photoTimeline[i].start && time < photoTimeline[i].end) {
        return i;
      }
    }
    return Math.max(0, photoTimeline.length - 1);
  };
  
  // Start preview playback. Allows preview without song now: clip audio plays
  // through unmuted <video data-preview-video> elements; song (if any) ducks
  // to 30 % during clip playback windows to mirror render-time behavior.
  const startPreview = () => {
    if (photos.length === 0) return;
    
    setPreviewPlaying(true);
    setPreviewCurrentTime(musicStartTime || 0);
    setPreviewPhotoIndex(0);
    
    // Start song audio if a song is selected
    if (selectedSong?.audio_url && previewAudioRef.current) {
      previewAudioRef.current.currentTime = musicStartTime || 0;
      previewAudioRef.current.volume = 1.0;
      previewAudioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
    
    // Start timer
    previewIntervalRef.current = setInterval(() => {
      setPreviewCurrentTime(prev => {
        const newTime = prev + 0.1;
        if (newTime >= totalDuration + (musicStartTime || 0)) {
          stopPreview();
          return prev;
        }
        // Update photo index
        const relativeTime = newTime - (musicStartTime || 0);
        setPreviewPhotoIndex(getCurrentPhotoIndex(relativeTime));
        return newTime;
      });
    }, 100);
  };
  
  // Stop preview
  const stopPreview = () => {
    setPreviewPlaying(false);
    // Pause ALL preview audio elements (there are two rendered: inline + modal).
    // React's ref only holds the last-mounted one, so we explicitly pause both.
    document.querySelectorAll('audio[data-preview-audio]').forEach((a) => {
      try { a.pause(); } catch (e) { /* ignore */ }
    });
    if (previewAudioRef.current) {
      try { previewAudioRef.current.pause(); } catch (e) { /* ignore */ }
    }
    if (previewIntervalRef.current) {
      clearInterval(previewIntervalRef.current);
    }
    // Pause any playing video clips in preview
    document.querySelectorAll('[data-preview-video]').forEach(v => v.pause());
  };
  
  // Toggle preview play/pause
  const togglePreview = () => {
    if (previewPlaying) {
      stopPreview();
    } else {
      if (previewCurrentTime >= totalDuration + (musicStartTime || 0)) {
        // Restart from beginning
        startPreview();
      } else {
        // Resume
        setPreviewPlaying(true);
        if (previewAudioRef.current) {
          previewAudioRef.current.play().catch(e => console.log('Audio play failed:', e));
        }
        previewIntervalRef.current = setInterval(() => {
          setPreviewCurrentTime(prev => {
            const newTime = prev + 0.1;
            if (newTime >= totalDuration + (musicStartTime || 0)) {
              stopPreview();
              return prev;
            }
            const relativeTime = newTime - (musicStartTime || 0);
            setPreviewPhotoIndex(getCurrentPhotoIndex(relativeTime));
            return newTime;
          });
        }, 100);
      }
    }
  };
  
  // Seek preview
  const seekPreview = (time) => {
    setPreviewCurrentTime(time);
    const relativeTime = time - (musicStartTime || 0);
    setPreviewPhotoIndex(getCurrentPhotoIndex(Math.max(0, relativeTime)));
    if (previewAudioRef.current) {
      previewAudioRef.current.currentTime = time;
    }
  };
  
  // Close preview
  const closePreview = () => {
    stopPreview();
    setShowPreview(false);
    setPreviewCurrentTime(0);
    setPreviewPhotoIndex(0);
  };

  // Esc key closes the fullscreen preview overlay (consistent with other players)
  useEffect(() => {
    if (!showPreview) return;
    const onKey = (e) => { if (e.key === 'Escape') closePreview(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPreview]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Cleanup on unmount — pause audio + videos so sound doesn't bleed into other
  // pages when the user navigates away mid-preview (e.g., browser back button,
  // tab close, or route change).
  useEffect(() => {
    return () => {
      if (previewIntervalRef.current) {
        clearInterval(previewIntervalRef.current);
      }
      try {
        document.querySelectorAll('audio[data-preview-audio]').forEach((a) => {
          try { a.pause(); a.currentTime = 0; } catch (e) { /* ignore */ }
        });
        document.querySelectorAll('video[data-preview-video]').forEach((v) => {
          try { v.pause(); } catch (e) { /* ignore */ }
        });
        if (previewAudioRef.current) {
          try {
            previewAudioRef.current.pause();
            previewAudioRef.current.currentTime = 0;
          } catch (e) { /* ignore */ }
        }
      } catch (e) { /* ignore */ }
    };
  }, []);

  // Sync preview videos with playback state: play when previewPlaying, pause otherwise.
  // Also seeks into the clip based on where the timeline cursor sits within its slide.
  useEffect(() => {
    const currentPhoto = photos[previewPhotoIndex];
    if (!currentPhoto || currentPhoto.type !== 'video') return;

    const slide = photoTimeline[previewPhotoIndex];
    if (!slide) return;
    // Relative position within this slide (in seconds, from slide start)
    const relWithinSlide = Math.max(
      0,
      previewCurrentTime - (musicStartTime || 0) - slide.start
    );
    const inP = currentPhoto.inPoint ?? 0;
    const seekTo = inP + Math.min(relWithinSlide, slide.duration);

    document.querySelectorAll('video[data-preview-video]').forEach((v) => {
      try {
        // Unmute clip audio so the user hears what the render will sound like.
        v.muted = false;
        // Seek on mount/change or when paused
        if (!previewPlaying) {
          if (Math.abs(v.currentTime - seekTo) > 0.1) v.currentTime = seekTo;
          v.pause();
        } else {
          // When starting to play, align to expected time then play
          if (Math.abs(v.currentTime - seekTo) > 0.3) v.currentTime = seekTo;
          v.play().catch(() => {});
        }
      } catch (e) { /* ignore */ }
    });

    // Auto-duck the song under the clip — drops to 30 % during a video slide,
    // restores to 100 % during photos. Mirrors the HP render-time behavior
    // (audio_mix_mode = "duck").
    if (previewAudioRef.current) {
      try {
        previewAudioRef.current.volume = currentPhoto.type === 'video' ? 0.3 : 1.0;
      } catch (e) { /* ignore */ }
    }
  }, [previewPlaying, previewPhotoIndex, previewCurrentTime, photos, photoTimeline, musicStartTime]);

  // Generate slideshow
  const handleGenerate = async () => {
    // Audio rules (per user spec):
    //   • No song selected  → render uses clip audio only (no song layered)
    //   • Song + clips with audio → ducked mix (song dips to 30 % during clips)
    //   • Song + only photos → song plays at full volume
    if (!selectedSong && photos.length === 0) {
      setError('Please upload at least 1 photo');
      return;
    }
    if (photos.length < 1) {
      setError('Please upload at least 1 photo');
      return;
    }
    
    setGenerating(true);
    setError('');
    
    try {
      const slideDuration = isAdvancedMode ? globalDuration : QUICK_MODE_DURATION;
      
      const { data } = await apiFetch('/slideshow/create', {
        method: 'POST',
        body: JSON.stringify({
          song_id: selectedSong?.song_id || null,
          photo_urls: photos.map(p => p.url),
          clip_types: photos.map(p => p.type || 'photo'),
          clip_in_points: photos.map(p => p.inPoint || 0),
          clip_out_points: photos.map(p => p.outPoint || 0),
          photo_durations: isAdvancedMode && usePerSlideTiming 
            ? photos.map(p => p.duration || slideDuration) 
            : null,
          photo_fit_modes: isAdvancedMode 
            ? photos.map(p => p.fitMode || globalFitMode) 
            : null,
          // Manual crop focus points per photo (null when in Auto mode or no crop set).
          // Forwarded to HP Server which applies them via ffmpeg crop filter.
          crop_mode: cropMode,
          crop_positions: cropMode === 'manual'
            ? photos.map(p => p.cropPosition || null)
            : null,
          title_text: null,
          credits_text: isAdvancedMode && endCardEnabled ? 'Wrapped In Music' : (endCardEnabled ? 'Wrapped In Music' : null),
          photo_duration: slideDuration,
          transition_type: isAdvancedMode ? transitionType : 'crossfade',
          ken_burns: isAdvancedMode ? kenBurnsEnabled : false,
          music_start_time: isAdvancedMode ? musicStartTime : 0,
          // Audio mixing (Phases 1-3):
          //   - clip_only:  no song selected → clip audio plays
          //   - song_only:  song + photos only (no video clips OR all clips muted)
          //   - duck:       song + clip audio (clip-audio enabled) AND duck toggle ON
          //   - both_full:  song + clip audio AND duck toggle OFF (both 100%)
          //
          // Per-clip audio enabled flag: forwarded as `clip_audio_enabled` so
          // HP v2.2+ can selectively mute individual clips in the final mix.
          audio_mix_mode: (() => {
            const hasAudibleClip = photos.some(p => p.type === 'video' && p.audioEnabled !== false);
            if (!selectedSong) return 'clip_only';
            if (!hasAudibleClip) return 'song_only';
            return duckSongEnabled ? 'duck' : 'both_full';
          })(),
          clip_audio_enabled: photos.map(p => p.type === 'video' ? (p.audioEnabled !== false) : false),
          include_watermark: endCardEnabled,
          orientation: orientation
        })
      });
      
      setVideoId(data.video_id);
      setVideoStatus({ status: 'pending' });
      
      // Store render info for display
      const numPhotos = photos.filter(p => (p.type || 'photo') === 'photo').length;
      const numVideos = photos.filter(p => p.type === 'video').length;
      setRenderInfo({ numPhotos, numVideos, estimatedTime: data.estimated_duration });
    } catch (err) {
      console.error('Generation failed:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to start video generation');
      setGenerating(false);
    }
  };

  // Render timer
  const [renderStartTime, setRenderStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [renderInfo, setRenderInfo] = useState(null);
  
  // Start timer when generating begins
  useEffect(() => {
    if (generating && !renderStartTime) {
      setRenderStartTime(Date.now());
    }
    if (!generating) {
      setRenderStartTime(null);
      setElapsedTime(0);
    }
  }, [generating]);
  
  // Update elapsed time every second
  useEffect(() => {
    if (!renderStartTime) return;
    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - renderStartTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [renderStartTime]);

  const videoCost = settings?.video_generation || 20;

  return (
    <div className="min-h-screen bg-background">
      <MainNav />
      
      <main className="container-wide py-8 pt-24">
        <div className="max-w-4xl mx-auto section-gap">
          {/* Header with Mode Toggle */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
              <h1 className="page-title">Create Video</h1>
              
              {/* Mode Toggle */}
              {!videoStatus && (
                <div className="flex items-center gap-1 bg-white/[0.03] border border-white/10 rounded-full p-1">
                  <button
                    onClick={() => setIsAdvancedMode(false)}
                    title="5s per photo • Auto-fit • Smooth crossfade • Music fades at end"
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      !isAdvancedMode 
                        ? 'bg-gradient-to-br from-[#915ee9] to-[#7d46d9] text-white shadow-[0_4px_14px_-2px_rgba(145,94,233,0.4)]' 
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    <Zap className="h-4 w-4" />
                    Quick
                  </button>
                  <button
                    onClick={() => setIsAdvancedMode(true)}
                    title="Custom timing • Per-photo settings • Ken Burns effect • Transitions"
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      isAdvancedMode 
                        ? 'bg-gradient-to-br from-[#915ee9] to-[#7d46d9] text-white shadow-[0_4px_14px_-2px_rgba(145,94,233,0.4)]' 
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    <Settings className="h-4 w-4" />
                    Advanced
                  </button>
                </div>
              )}
            </div>
            <p className="body-text">
              {isAdvancedMode 
                ? 'Full control over timing, transitions, and photo settings'
                : 'Quick and easy — just pick a song and upload photos'
              }
            </p>
          </div>

          {/* Video Generation Status */}
          {videoStatus && (
            <div className="card-standard p-8">
              {videoStatus.status === 'pending' || videoStatus.status === 'processing' ? (
                <div className="text-center">
                  <Loader2 className="h-12 w-12 animate-spin text-[#915ee9] mx-auto mb-4" />
                  <h3 className="section-title mb-2">Creating your video…</h3>
                  {renderInfo && (
                    <p className="body-text mb-3">
                      Rendering {renderInfo.numPhotos + renderInfo.numVideos} media items
                      {renderInfo.estimatedTime ? ` — estimated ${Math.floor(renderInfo.estimatedTime / 60)}:${String(Math.round(renderInfo.estimatedTime % 60)).padStart(2, '0')}` : ''}
                    </p>
                  )}
                  {elapsedTime > 0 && (
                    <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-full">
                      <Timer className="h-4 w-4 text-[#915ee9]" />
                      <span className="text-white font-mono text-lg">
                        {Math.floor(elapsedTime / 60)}:{String(elapsedTime % 60).padStart(2, '0')}
                      </span>
                    </div>
                  )}
                  {videoStatus.progress && (
                    <p className="helper-text mt-3">{videoStatus.progress}</p>
                  )}
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={handleCancelRender}
                      disabled={cancellingRender}
                      data-testid="slideshow-cancel-btn"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 hover:border-red-500/60 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {cancellingRender ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Cancelling…
                        </>
                      ) : (
                        <>
                          <X className="h-4 w-4" />
                          Cancel Render
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : videoStatus.status === 'completed' ? (
                <div className="text-center">
                  <Sparkles className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="section-title mb-2">Video ready!</h3>
                  <p className="body-text mb-6">Your slideshow has been created successfully.</p>
                  <div className="flex gap-3 justify-center flex-wrap">
                    <a 
                      href={videoStatus.video_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-primary inline-flex items-center gap-2 px-5 py-2.5"
                    >
                      <Play className="h-4 w-4" /> Watch Video
                    </a>
                    <button 
                      onClick={async () => {
                        const url = videoStatus?.video_url;
                        if (!url) return;
                        try {
                          const proxyUrl = `${API}/api/videos/${videoId}/download`;
                          const token = getStoredToken();
                          const resp = await fetch(proxyUrl, {
                            credentials: 'include',
                            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                          });
                          if (!resp.ok) throw new Error('Download failed');
                          const blob = await resp.blob();
                          const blobUrl = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = blobUrl;
                          link.download = `slideshow_${videoId || 'video'}.mp4`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(blobUrl);
                        } catch (err) {
                          // Fallback: open URL directly
                          window.open(url, '_blank');
                        }
                      }}
                      className="btn-secondary inline-flex items-center gap-2 px-5 py-2.5"
                    >
                      Download MP4
                    </button>
                    <button
                      onClick={() => {
                        setVideoId(null);
                        setVideoStatus(null);
                      }}
                      className="btn-secondary inline-flex items-center gap-2 px-5 py-2.5"
                    >
                      Edit & Retry
                    </button>
                    <button
                      onClick={() => {
                        setVideoId(null);
                        setVideoStatus(null);
                        setPhotos([]);
                        setSelectedSong(null);
                      }}
                      className="btn-secondary inline-flex items-center gap-2 px-5 py-2.5"
                    >
                      Start Fresh
                    </button>
                  </div>
                </div>
              ) : videoStatus.status === 'failed' ? (
                <div className="text-center">
                  <div className={`h-12 w-12 rounded-full border flex items-center justify-center mx-auto mb-4 ${videoStatus.cancelled ? 'bg-zinc-500/15 border-zinc-500/30' : 'bg-red-500/15 border-red-500/30'}`}>
                    {videoStatus.cancelled ? (
                      <X className="h-6 w-6 text-zinc-300" />
                    ) : (
                      <AlertTriangle className="h-6 w-6 text-red-400" />
                    )}
                  </div>
                  <h3 className="section-title mb-2">
                    {videoStatus.cancelled ? 'Render cancelled' : 'Generation failed'}
                  </h3>
                  <p className={`text-sm mb-5 ${videoStatus.cancelled ? 'text-zinc-400' : 'text-red-400'}`}>
                    {videoStatus.cancelled
                      ? 'This render was cancelled before it finished. Save a draft to pick up here later.'
                      : (videoStatus.error || 'Something went wrong')}
                  </p>
                  <div className="flex items-center gap-3 justify-center flex-wrap">
                    {videoStatus.cancelled && (
                      <button
                        onClick={handleSaveDraft}
                        disabled={savingDraft}
                        className="btn-secondary inline-flex items-center gap-2 px-5 py-2.5"
                        data-testid="save-draft-from-cancel-btn"
                      >
                        {savingDraft
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <Save className="h-4 w-4" />
                        }
                        Save Draft
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setVideoId(null);
                        setVideoStatus(null);
                        setGenerating(false);
                      }}
                      className="btn-primary inline-flex items-center gap-2 px-5 py-2.5"
                      data-testid="slideshow-try-again-btn"
                    >
                      {videoStatus.cancelled ? 'Back to Editor' : 'Try Again'}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Main Form */}
          {!videoStatus && (
            <div className="flex flex-col gap-6">
              {/* Top basics row: Song + Format — 2-col grid, each card constrained & centered */}
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 justify-items-center order-0"
                data-testid="basics-row"
              >
                {/* Selected Song card — entire card is the click target */}
                <button
                  type="button"
                  onClick={() => songs.length === 0 ? navigate('/create-song') : setShowSongDrawer(true)}
                  data-testid="selected-song-card"
                  className="flex items-center gap-4 p-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#915ee9]/50 hover:bg-white/[0.04] transition-colors duration-200 w-full max-w-sm text-left cursor-pointer focus:outline-none focus:border-[#915ee9]/70 focus:ring-1 focus:ring-[#915ee9]/40"
                >
                  {/* Cover / Placeholder */}
                  <div className="w-[150px] h-[150px] rounded-lg overflow-hidden flex-shrink-0 bg-zinc-900 border border-white/8">
                    {selectedSong?.image_url ? (
                      <img
                        src={selectedSong.image_url}
                        alt={selectedSong.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center p-[11px]">
                        <img
                          src="/assets/migrated/music-notation.png"
                          alt="Music notation"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                    {selectedSong ? (
                      <>
                        <p className="text-[11px] uppercase tracking-wider text-zinc-500">Song</p>
                        <h3 className="text-white font-semibold text-base leading-tight truncate">
                          {selectedSong.title || 'Untitled'}
                        </h3>
                        {selectedSong.duration ? (
                          <p className="text-xs text-zinc-400">{formatDuration(selectedSong.duration)}</p>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-zinc-300">No song selected</p>
                        <p className="text-xs text-zinc-500">
                          {songs.length === 0 ? 'Create a song first' : 'Optional — leave empty to use clip audio only'}
                        </p>
                      </>
                    )}
                  </div>
                </button>

                {/* Audio mode hint — explains what the user will hear in the
                    final render based on what they've selected. Updates live
                    as they add/remove songs and video clips. */}
                {(selectedSong || photos.some(p => p.type === 'video')) && (() => {
                  const hasAudibleClip = photos.some(p => p.type === 'video' && p.audioEnabled !== false);
                  return (
                    <div
                      className="text-[11px] text-zinc-400 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5 max-w-sm space-y-2"
                      data-testid="audio-mode-hint"
                    >
                      {selectedSong && hasAudibleClip ? (
                        duckSongEnabled ? (
                          <>🔊 <span className="text-emerald-300">Auto-duck mix</span> — song plays full volume on photos, dips to 30 % during video clips so clip audio comes through.</>
                        ) : (
                          <>🔊🔊 <span className="text-amber-300">Both full</span> — song and clip audio both at 100 %. Can sound chaotic; great for voiceover-on-instrumental.</>
                        )
                      ) : selectedSong ? (
                        <>🎵 <span className="text-emerald-300">Song only</span> — your song plays at full volume throughout.</>
                      ) : (
                        <>🎬 <span className="text-emerald-300">Clip audio only</span> — no song; output uses audio from your video clips. Photo slides will be silent.</>
                      )}
                      {/* Phase 3 — global duck toggle. Only shown when both song
                          AND at least one audio-enabled clip exist (otherwise it
                          has no effect). */}
                      {selectedSong && hasAudibleClip && (
                        <label className="flex items-center gap-2 cursor-pointer select-none pt-1 border-t border-white/5">
                          <input
                            type="checkbox"
                            checked={duckSongEnabled}
                            onChange={(e) => setDuckSongEnabled(e.target.checked)}
                            className="w-3.5 h-3.5 rounded bg-zinc-800 border-zinc-600 text-emerald-500"
                            data-testid="duck-song-toggle"
                          />
                          <span className="text-zinc-300 text-[11px]">
                            Duck song during clip audio
                          </span>
                        </label>
                      )}
                    </div>
                  );
                })()}

                {/* Video Format card — entire card is the click target */}
                {(() => {
                  const fmt = getFormatById(orientation);
                  return (
                    <button
                      type="button"
                      onClick={() => setShowFormatDrawer(true)}
                      data-testid="video-format-summary-card"
                      className="flex items-center gap-4 p-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#915ee9]/50 hover:bg-white/[0.04] transition-colors duration-200 w-full max-w-sm text-left cursor-pointer focus:outline-none focus:border-[#915ee9]/70 focus:ring-1 focus:ring-[#915ee9]/40"
                    >
                      {/* Silhouette slot */}
                      <div className="w-[150px] h-[150px] rounded-lg bg-zinc-900 border border-white/8 flex items-center justify-center flex-shrink-0">
                        <div
                          className="rounded-md bg-gradient-to-br from-[#915ee9] to-[#7d46d9]"
                          style={{ width: fmt.sw, height: fmt.sh }}
                        />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                        <p className="text-[11px] uppercase tracking-wider text-zinc-500">Format</p>
                        <h3 className="text-white font-semibold text-base leading-tight truncate">
                          {fmt.label}
                        </h3>
                        <p className="text-xs text-zinc-400">
                          {fmt.ratio} · {fmt.helper}
                        </p>
                      </div>
                    </button>
                  );
                })()}
              </div>

              {/* Step 3: Upload Photos */}
              <section className="card-standard p-6 order-3">
                <div className="flex items-center gap-3 mb-5">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#915ee9]/15 border border-[#915ee9]/40 text-[#b094f2] text-sm font-semibold">3</span>
                  <h2 className="section-title flex items-center gap-2">
                    <ImageIcon className="h-5 w-5 text-[#915ee9]" />
                    Upload media
                  </h2>
                  <span className="helper-text ml-auto">Max 50 items</span>
                </div>
                
                {/* Upload Area - Compact single-line with drag & drop */}
                <label 
                  className="flex items-center gap-3 bg-white/[0.02] border border-dashed border-white/15 rounded-xl px-4 py-4 cursor-pointer hover:border-[#915ee9]/50 hover:bg-[#915ee9]/[0.04] transition-all mb-3"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/mp4,video/quicktime"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploading || photos.length >= 50}
                  />
                  {uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-[#915ee9] flex-shrink-0" />
                  ) : (
                    <Upload className="h-5 w-5 text-zinc-400 flex-shrink-0" />
                  )}
                  <span className="text-white text-sm">
                    {uploading ? 'Uploading…' : 'Click or drag photos & videos here'}
                  </span>
                  <span className="helper-text ml-auto" data-testid="upload-limits-hint">
                    {photos.length}/50 • Photos up to {uploadLimits.max_photo_mb}MB • Videos up to {uploadLimits.max_video_mb}MB
                  </span>
                </label>

                {/* Auto / Manual crop mode toggle */}
                <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-xl bg-white/[0.02] border border-white/10">
                  <div className="flex items-center gap-2 min-w-0">
                    <Crop className="h-4 w-4 text-[#b094f2] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium leading-tight">Crop</p>
                      <p className="text-[11px] text-zinc-500 leading-tight truncate">
                        {cropMode === 'auto'
                          ? 'Smart crop (centered, top-bias for portraits)'
                          : 'Click any photo to position the crop manually'}
                      </p>
                    </div>
                  </div>
                  <div
                    role="tablist"
                    className="inline-flex rounded-lg border border-white/10 bg-black/40 p-0.5 flex-shrink-0"
                    data-testid="crop-mode-toggle"
                  >
                    <button
                      type="button"
                      role="tab"
                      onClick={() => setCropMode('auto')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        cropMode === 'auto'
                          ? 'bg-[#915ee9] text-white'
                          : 'text-zinc-400 hover:text-white'
                      }`}
                      data-testid="crop-mode-auto-btn"
                    >
                      Auto
                    </button>
                    <button
                      type="button"
                      role="tab"
                      onClick={() => setCropMode('manual')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        cropMode === 'manual'
                          ? 'bg-[#915ee9] text-white'
                          : 'text-zinc-400 hover:text-white'
                      }`}
                      data-testid="crop-mode-manual-btn"
                    >
                      Manual
                    </button>
                  </div>
                </div>

                {/* AI Generate Button */}
                <button
                  onClick={() => setShowAIGenerate(true)}
                  data-testid="ai-generate-open-btn"
                  className="group w-full flex items-center justify-center gap-2 p-3 mb-4 rounded-xl transition-all relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, rgba(145,94,233,0.12) 0%, rgba(125,70,217,0.08) 100%)',
                    border: '1px solid rgba(145,94,233,0.35)',
                  }}
                >
                  <Sparkles className="h-4 w-4 text-[#b094f2] group-hover:scale-110 transition-transform" />
                  <span className="text-[#b094f2] font-medium text-sm">AI Generate Video Clip or Image</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#915ee9]/0 via-[#915ee9]/10 to-[#915ee9]/0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                </button>

                {/* Photo Grid */}
                {photos.length > 0 && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {photos.map((photo, index) => (
                      <div 
                        key={photo.id} 
                        className={`relative group cursor-pointer rounded-lg overflow-hidden ${
                          isAdvancedMode && selectedPhotoIndex === index 
                            ? 'ring-2 ring-[#915ee9] shadow-[0_0_0_1px_rgba(145,94,233,0.4)]' 
                            : ''
                        }`}
                        onClick={() => isAdvancedMode && setSelectedPhotoIndex(index)}
                      >
                        {photo.type === 'video' ? (
                          <video 
                            src={photo.url} 
                            className="aspect-square object-cover rounded-lg"
                            style={{ objectPosition: objectPositionFor(photo) }}
                            muted
                          />
                        ) : (
                          <img 
                            src={photo.url} 
                            alt={`Photo ${index + 1}`}
                            className="aspect-square object-cover rounded-lg"
                            style={{ objectPosition: objectPositionFor(photo) }}
                          />
                        )}
                        {/* Manual-crop badge (only when user has set a focus point) */}
                        {cropMode === 'manual' && photo.cropPosition && (
                          <span
                            className="absolute top-1 right-1 text-[9px] bg-emerald-500/90 px-1 py-0.5 rounded text-white font-medium flex items-center gap-0.5"
                            title="Custom crop set"
                            data-testid={`crop-set-badge-${index}`}
                          >
                            <Crop className="h-2 w-2" />
                          </span>
                        )}
                        {photo.type === 'video' && (() => {
                          const full = photo.videoDuration || 0;
                          const inP = photo.inPoint ?? 0;
                          const outP = photo.outPoint ?? full;
                          const effective = Math.max(0, outP - inP);
                          const trimmed = full > 0 && (inP > 0.05 || (full - outP) > 0.05);
                          const audioOn = photo.audioEnabled !== false;
                          return (
                            <>
                              <span
                                className="absolute top-1 left-1 text-[9px] bg-[#915ee9]/90 px-1.5 py-0.5 rounded text-white font-medium flex items-center gap-0.5"
                                title={trimmed ? `Trimmed ${effective.toFixed(1)}s of ${full.toFixed(1)}s` : `${effective.toFixed(1)}s full clip`}
                              >
                                {trimmed ? '✂' : <Play className="h-2 w-2" />}
                                {Math.round(effective)}s
                              </span>
                              {/* Per-clip audio toggle (Phase 2) — click to mute/unmute clip
                                  audio in the final render. Visible at all times so users
                                  can decide before render. Does NOT affect preview, only
                                  HP-side mixing. */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPhotos(prev => prev.map((p, i) => (
                                    i === index ? { ...p, audioEnabled: !audioOn } : p
                                  )));
                                }}
                                className={`absolute bottom-1 left-1 p-1 rounded text-white transition-colors ${
                                  audioOn
                                    ? 'bg-emerald-500/80 hover:bg-emerald-500'
                                    : 'bg-zinc-700/80 hover:bg-zinc-700'
                                }`}
                                title={audioOn ? 'Clip audio: ON (click to mute)' : 'Clip audio: MUTED (click to unmute)'}
                                data-testid={`clip-audio-toggle-${index}`}
                              >
                                {audioOn ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />}
                              </button>
                            </>
                          );
                        })()}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                          {/* Navigation arrows at center */}
                          <div className="absolute inset-0 flex items-center justify-center gap-2">
                            {index > 0 && (
                              <button 
                                onClick={(e) => { e.stopPropagation(); movePhoto(index, -1); }}
                                className="p-1 bg-white/20 rounded hover:bg-white/40"
                              >
                                <ChevronLeft className="h-3 w-3 text-white" />
                              </button>
                            )}
                            {index < photos.length - 1 && (
                              <button 
                                onClick={(e) => { e.stopPropagation(); movePhoto(index, 1); }}
                                className="p-1 bg-white/20 rounded hover:bg-white/40"
                              >
                                <ChevronRight className="h-3 w-3 text-white" />
                              </button>
                            )}
                          </div>
                          {/* Delete button at bottom-right corner */}
                          <button 
                            onClick={(e) => { e.stopPropagation(); removePhoto(index); }}
                            className="absolute bottom-1 right-1 p-1 bg-red-500/80 rounded hover:bg-red-500"
                          >
                            <Trash2 className="h-3 w-3 text-white" />
                          </button>
                          {/* Edit-crop button (manual mode, photos only) */}
                          {cropMode === 'manual' && photo.type === 'photo' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setCropEditorPhotoIndex(index); }}
                              className="absolute bottom-1 left-1 p-1 bg-emerald-500/90 rounded hover:bg-emerald-500"
                              title="Adjust crop"
                              data-testid={`edit-crop-btn-${index}`}
                            >
                              <Crop className="h-3 w-3 text-white" />
                            </button>
                          )}
                        </div>
                        <span className="absolute bottom-1 left-1 text-[10px] bg-black/70 px-1 rounded text-white">
                          {index + 1}
                        </span>
                        {isAdvancedMode && usePerSlideTiming && (
                          <span className="absolute top-1 right-1 text-[10px] bg-primary/80 px-1 rounded text-white">
                            {photo.duration || globalDuration}s
                          </span>
                        )}
                      </div>
                    ))}
                    {photos.length < 50 && (
                      <label className="aspect-square border-2 border-dashed border-zinc-700 rounded-lg flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
                        <input
                          type="file"
                          multiple
                          accept="image/*,video/mp4,video/quicktime"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          disabled={uploading}
                        />
                        <Plus className="h-6 w-6 text-zinc-500" />
                      </label>
                    )}
                  </div>
                )}
              </section>
              
              {/* Per-Slide Editor — appears below media grid, close to slides */}
              {selectedPhotoIndex !== null && photos[selectedPhotoIndex] && (
                <section className="card-standard p-5 order-3" data-testid="per-slide-editor">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="section-title flex items-center gap-2 text-base">
                      {photos[selectedPhotoIndex].type === 'video' ? (
                        <>
                          <Play className="h-4 w-4 text-[#915ee9]" />
                          Trim video clip {selectedPhotoIndex + 1}
                        </>
                      ) : (
                        <>
                          <ImageIcon className="h-4 w-4 text-[#915ee9]" />
                          Slide {selectedPhotoIndex + 1} settings
                        </>
                      )}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setSelectedPhotoIndex(null)}
                      className="text-zinc-400 hover:text-white text-sm px-2 py-1 rounded-md hover:bg-white/5"
                    >
                      ✕
                    </button>
                  </div>

                  {photos[selectedPhotoIndex].type === 'video' ? (
                    <VideoTrimmer
                      src={photos[selectedPhotoIndex].url}
                      duration={photos[selectedPhotoIndex].videoDuration || 10}
                      inPoint={photos[selectedPhotoIndex].inPoint ?? 0}
                      outPoint={photos[selectedPhotoIndex].outPoint ?? (photos[selectedPhotoIndex].videoDuration || 10)}
                      onChange={(nextIn, nextOut) => {
                        updatePhotoSetting(selectedPhotoIndex, 'inPoint', Math.round(nextIn * 10) / 10);
                        updatePhotoSetting(selectedPhotoIndex, 'outPoint', Math.round(nextOut * 10) / 10);
                      }}
                      onClose={() => setSelectedPhotoIndex(null)}
                    />
                  ) : (
                    <div className="space-y-4">
                      {isAdvancedMode && usePerSlideTiming && (
                        <div>
                          <Label className="text-zinc-300 text-sm mb-2 block">
                            Duration: {photos[selectedPhotoIndex].duration || globalDuration}s
                          </Label>
                          <Slider
                            value={[photos[selectedPhotoIndex].duration || globalDuration]}
                            onValueChange={([val]) => updatePhotoSetting(selectedPhotoIndex, 'duration', val)}
                            min={1}
                            max={20}
                            step={0.5}
                          />
                        </div>
                      )}
                      {isAdvancedMode && (
                        <div>
                          <Label className="text-zinc-300 text-sm mb-2 block">Fit mode</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { id: 'cover', label: 'Fill' },
                              { id: 'contain', label: 'Fit' },
                            ].map((opt) => {
                              const current = photos[selectedPhotoIndex].fitMode || globalFitMode;
                              const selected = current === opt.id;
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  onClick={() => updatePhotoSetting(selectedPhotoIndex, 'fitMode', opt.id)}
                                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                    selected
                                      ? 'border-[#915ee9] bg-[#915ee9]/10 text-white'
                                      : 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-[#915ee9]/40'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {!isAdvancedMode && (
                        <p className="helper-text">Switch to Advanced mode to customize this slide.</p>
                      )}
                    </div>
                  )}
                </section>
              )}
              
              {/* Inline Preview Player */}
              {photos.length > 0 && selectedSong && (
                <section className="card-standard p-5 order-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="section-title flex items-center gap-2 text-base">
                      <Eye className="h-4 w-4 text-[#915ee9]" />
                      Preview
                    </h3>
                    <div className="flex items-center gap-3">
                      <span className="helper-text">
                        Total: {formatDuration(totalDuration)}
                      </span>
                      <button
                        type="button"
                        onClick={() => { stopPreview(); setShowPreview(true); }}
                        title="Enlarge preview"
                        data-testid="preview-enlarge-btn"
                        className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-[#915ee9]/15 transition-colors"
                      >
                        <Maximize2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    {/* Preview Window */}
                    <div 
                      className={`relative bg-black rounded-lg overflow-hidden flex-shrink-0 ${
                        orientation === 'portrait' ? 'h-[420px] aspect-[9/16]' : 
                        orientation === 'square' ? 'h-56 aspect-square' : 
                        'w-72 aspect-video'
                      }`}
                    >
                      {photos[previewPhotoIndex] && (
                        photos[previewPhotoIndex].type === 'video' ? (
                          <video
                            src={photos[previewPhotoIndex].url}
                            playsInline
                            data-preview-video
                            key={`inline-${previewPhotoIndex}`}
                            className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
                              (photos[previewPhotoIndex].fitMode || globalFitMode) === 'contain' 
                                ? 'object-contain' 
                                : 'object-cover'
                            }`}
                            style={{
                              objectPosition: (photos[previewPhotoIndex].fitMode || globalFitMode) === 'contain'
                                ? undefined
                                : objectPositionFor(photos[previewPhotoIndex]),
                            }}
                          />
                        ) : (
                          <img
                            src={photos[previewPhotoIndex].url}
                            alt={`Slide ${previewPhotoIndex + 1}`}
                            className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
                              (photos[previewPhotoIndex].fitMode || globalFitMode) === 'contain' 
                                ? 'object-contain' 
                                : 'object-cover'
                            }`}
                            style={{
                              objectPosition: (photos[previewPhotoIndex].fitMode || globalFitMode) === 'contain'
                                ? undefined
                                : objectPositionFor(photos[previewPhotoIndex]),
                            }}
                          />
                        )
                      )}
                      <div className="absolute bottom-1 right-1 bg-black/70 px-1.5 py-0.5 rounded text-[10px] text-white">
                        {previewPhotoIndex + 1}/{photos.length}
                      </div>
                    </div>
                    
                    {/* Controls & Timeline */}
                    <div className="flex-1 min-w-0">
                      {/* Play/Pause & Time */}
                      <div className="flex items-center gap-3 mb-2">
                        <button
                          onClick={togglePreview}
                          className="w-8 h-8 rounded-full bg-primary flex items-center justify-center hover:bg-primary/80 transition-colors flex-shrink-0"
                        >
                          {previewPlaying ? (
                            <Pause className="h-4 w-4 text-white" />
                          ) : (
                            <Play className="h-4 w-4 text-white ml-0.5" />
                          )}
                        </button>
                        
                        <div className="text-xs text-zinc-400 font-mono w-24">
                          {formatDuration(Math.max(0, previewCurrentTime - (musicStartTime || 0)))} / {formatDuration(totalDuration)}
                        </div>
                        
                        {/* Progress bar */}
                        <input
                          type="range"
                          min={0}
                          max={totalDuration}
                          step={0.1}
                          value={Math.max(0, previewCurrentTime - (musicStartTime || 0))}
                          onChange={(e) => seekPreview(parseFloat(e.target.value) + (musicStartTime || 0))}
                          className="flex-1 accent-primary h-1"
                        />
                      </div>
                      
                      {/* Photo Timeline Bar */}
                      <div className="relative h-6 bg-zinc-800 rounded overflow-hidden">
                        {photoTimeline.map((photo, i) => (
                          <div
                            key={photo.id ?? `slot-${i}`}
                            onClick={() => {
                              seekPreview(photo.start + (musicStartTime || 0));
                              setPreviewPhotoIndex(i);
                            }}
                            className={`absolute top-0 bottom-0 border-r border-zinc-600 cursor-pointer flex items-center justify-center text-[10px] hover:opacity-80 ${
                              i === previewPhotoIndex ? 'bg-primary/50' : 'bg-zinc-700'
                            }`}
                            style={{
                              left: `${(photo.start / totalDuration) * 100}%`,
                              width: `${(photo.duration / totalDuration) * 100}%`
                            }}
                            title={`Photo ${i + 1}: ${photo.duration}s`}
                          >
                            <span className="text-white/70">{i + 1}</span>
                          </div>
                        ))}
                        {/* Playhead */}
                        <div 
                          className="absolute top-0 bottom-0 w-0.5 bg-white z-10 pointer-events-none"
                          style={{ 
                            left: `${((previewCurrentTime - (musicStartTime || 0)) / totalDuration) * 100}%` 
                          }}
                        />
                      </div>
                      
                      <p className="text-[10px] text-zinc-500 mt-1">
                        Click segments to jump • {isAdvancedMode && usePerSlideTiming ? 'Per-slide timing' : `${globalDuration}s per photo`}
                      </p>
                    </div>
                  </div>
                  
                  {/* Hidden audio element */}
                  <audio
                    ref={previewAudioRef}
                    data-preview-audio
                    src={selectedSong?.audio_url}
                    preload="auto"
                  />
                </section>
              )}

              {/* NEW: Video Settings Panel (placed BEFORE upload media) */}
              <div className="order-1">
                <VideoSettingsPanel
                  isAdvancedMode={isAdvancedMode}
                  transitionType={transitionType} setTransitionType={setTransitionType}
                  kenBurnsEnabled={kenBurnsEnabled} setKenBurnsEnabled={setKenBurnsEnabled}
                  musicStartTime={musicStartTime} setMusicStartTime={setMusicStartTime}
                  globalFitMode={globalFitMode} setGlobalFitMode={setGlobalFitMode}
                  globalDuration={globalDuration} setGlobalDuration={setGlobalDuration}
                  usePerSlideTiming={usePerSlideTiming} setUsePerSlideTiming={setUsePerSlideTiming}
                  endCardEnabled={endCardEnabled} setEndCardEnabled={setEndCardEnabled}
                  selectedSong={selectedSong}
                  videoCost={videoCost}
                />
                {/* Duration Estimate & Warning */}
                {photos.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-sm text-zinc-400">
                        <span className="text-white font-medium">Estimated video length:</span> {formatDuration(estimatedDuration)}
                        {selectedSong && (
                          <span className="text-zinc-500 ml-2">
                            (Song: {formatDuration(selectedSong.duration || 0)})
                          </span>
                        )}
                      </p>
                    </div>
                    {warning && (
                      <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-yellow-400">{warning}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>


              {/* Error Display */}
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 text-sm order-5">
                  {error}
                </div>
              )}
              
              {/* Quick Mode Warning - Show if user made Advanced changes but is in Quick mode */}
              {!isAdvancedMode && hasUsedAdvancedMode && hasAdvancedChanges && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl order-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-300 font-medium text-sm">You have Advanced mode settings</p>
                      <p className="text-amber-400/70 text-sm mt-1">
                        Quick mode will use preset timing (5s per photo, crossfade). 
                        To keep your custom settings, switch to Advanced mode before creating.
                      </p>
                      <button
                        onClick={() => setIsAdvancedMode(true)}
                        className="mt-2 text-sm text-amber-300 hover:text-amber-200 underline"
                      >
                        Switch to Advanced Mode
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Generate Button Row */}
              <div className="flex flex-wrap items-center justify-between gap-4 order-6 pt-2">
                <p className="body-text">
                  Cost: <span className="text-white font-semibold">{videoCost} credits</span>
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setShowDraftsDrawer(true)}
                    className="btn-secondary inline-flex items-center gap-2 px-4 py-2.5"
                    data-testid="open-drafts-btn"
                  >
                    <FolderOpen className="h-4 w-4" />
                    Drafts
                  </button>
                  <button
                    onClick={handleSaveDraft}
                    disabled={savingDraft || (!selectedSong && photos.length === 0)}
                    className="btn-secondary inline-flex items-center gap-2 px-4 py-2.5"
                    data-testid="save-draft-btn"
                  >
                    {savingDraft
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Save className="h-4 w-4" />
                    }
                    {currentDraftId ? 'Update Draft' : 'Save Draft'}
                  </button>
                  <button
                    onClick={() => { stopPreview(); setShowPreview(true); }}
                    disabled={photos.length < 1}
                    className="btn-secondary inline-flex items-center gap-2 px-5 py-2.5"
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={photos.length < 1 || generating}
                    className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-base"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Generating…
                      </>
                    ) : (
                      <>
                        <Video className="h-5 w-5" />
                        Create Slideshow
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Drafts drawer */}
        <DraftsDrawer
          open={showDraftsDrawer}
          onClose={() => setShowDraftsDrawer(false)}
          onLoadDraft={handleLoadDraft}
        />
        
        {/* Preview Modal */}
        {showPreview && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closePreview(); }}
            data-testid="slideshow-preview-overlay"
          >
            <div className="w-full max-w-4xl relative">
              {/* Close button — standard fullscreen-player exit (top-right, pill, visible on bright content) */}
              <button
                onClick={closePreview}
                aria-label="Close preview"
                title="Close (Esc)"
                data-testid="slideshow-preview-close-btn"
                className="absolute -top-2 -right-2 z-[60] p-2 rounded-full bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm border border-white/10 transition-colors shadow-lg"
              >
                <X className="h-5 w-5" />
              </button>
              
              {/* Preview title */}
              <div className="text-center mb-4">
                <h3 className="text-xl font-semibold text-white">Slideshow Preview</h3>
                <p className="text-zinc-400 text-sm">Preview timing before rendering</p>
              </div>
              
              {/* Preview window */}
              <div 
                className={`relative mx-auto bg-black rounded-lg overflow-hidden ${
                  orientation === 'portrait' ? 'aspect-[9/16] max-h-[80vh]' : 
                  orientation === 'square' ? 'aspect-square max-w-2xl' : 
                  'aspect-video max-w-4xl w-full'
                }`}
              >
                {/* Current photo */}
                {photos[previewPhotoIndex] && (
                  photos[previewPhotoIndex].type === 'video' ? (
                    <video
                      src={photos[previewPhotoIndex].url}
                      playsInline
                      data-preview-video
                      key={`large-${previewPhotoIndex}`}
                      className={`absolute inset-0 w-full h-full transition-opacity duration-500 ${
                        (photos[previewPhotoIndex].fitMode || globalFitMode) === 'contain' 
                          ? 'object-contain' 
                          : 'object-cover'
                      }`}
                      style={{
                        objectPosition: (photos[previewPhotoIndex].fitMode || globalFitMode) === 'contain'
                          ? undefined
                          : objectPositionFor(photos[previewPhotoIndex]),
                      }}
                    />
                  ) : (
                    <img
                      src={photos[previewPhotoIndex].url}
                      alt={`Slide ${previewPhotoIndex + 1}`}
                      className={`absolute inset-0 w-full h-full transition-opacity duration-500 ${
                        (photos[previewPhotoIndex].fitMode || globalFitMode) === 'contain' 
                          ? 'object-contain' 
                          : 'object-cover'
                      } ${kenBurnsEnabled ? 'animate-ken-burns' : ''}`}
                      style={{
                        objectPosition: (photos[previewPhotoIndex].fitMode || globalFitMode) === 'contain'
                          ? undefined
                          : objectPositionFor(photos[previewPhotoIndex]),
                      }}
                    />
                  )
                )}
                
                {/* Photo counter */}
                <div className="absolute top-3 right-3 bg-black/60 px-3 py-1 rounded-full text-white text-sm">
                  {previewPhotoIndex + 1} / {photos.length}
                </div>
                
                {/* Title overlay (if set) */}
                {/* Title text overlay removed — use AI-generated intro image instead */}
              </div>
              
              {/* Timeline */}
              <div className="mt-4 px-4">
                <div className="flex items-center gap-3 mb-2">
                  {/* Play/Pause */}
                  <button
                    onClick={togglePreview}
                    className="w-10 h-10 rounded-full bg-primary flex items-center justify-center hover:bg-primary/80 transition-colors"
                  >
                    {previewPlaying ? (
                      <Pause className="h-5 w-5 text-white" />
                    ) : (
                      <Play className="h-5 w-5 text-white ml-0.5" />
                    )}
                  </button>
                  
                  {/* Progress bar */}
                  <div className="flex-1">
                    <input
                      type="range"
                      min={0}
                      max={totalDuration + (musicStartTime || 0)}
                      step={0.1}
                      value={previewCurrentTime}
                      onChange={(e) => seekPreview(parseFloat(e.target.value))}
                      className="w-full accent-primary"
                    />
                  </div>
                  
                  {/* Time display */}
                  <div className="text-white text-sm font-mono min-w-[100px] text-right">
                    {formatDuration(previewCurrentTime)} / {formatDuration(totalDuration + (musicStartTime || 0))}
                  </div>
                </div>
                
                {/* Photo markers on timeline */}
                <div className="relative h-8 bg-zinc-800 rounded overflow-hidden">
                  {photoTimeline.map((photo, i) => (
                    <div
                      key={photo.id ?? `marker-${i}`}
                      className={`absolute top-0 bottom-0 border-r border-zinc-600 flex items-center justify-center text-xs ${
                        i === previewPhotoIndex ? 'bg-primary/40' : 'bg-zinc-700'
                      }`}
                      style={{
                        left: `${((photo.start + (musicStartTime || 0)) / (totalDuration + (musicStartTime || 0))) * 100}%`,
                        width: `${(photo.duration / (totalDuration + (musicStartTime || 0))) * 100}%`
                      }}
                    >
                      <span className="text-white/60 truncate px-1">{i + 1}</span>
                    </div>
                  ))}
                  {/* Playhead */}
                  <div 
                    className="absolute top-0 bottom-0 w-0.5 bg-white z-10"
                    style={{ 
                      left: `${(previewCurrentTime / (totalDuration + (musicStartTime || 0))) * 100}%` 
                    }}
                  />
                </div>
              </div>
              
              {/* Current photo timing info (Advanced mode) */}
              {isAdvancedMode && photoTimeline[previewPhotoIndex] && (
                <div className="mt-4 text-center text-zinc-400 text-sm">
                  Photo {previewPhotoIndex + 1}: {photoTimeline[previewPhotoIndex].duration}s
                  {usePerSlideTiming && ' (custom timing)'}
                </div>
              )}
            </div>
            
            {/* No duplicate audio here — the inline preview player owns the single
                <audio ref={previewAudioRef}> element, so audio follows whichever
                view is visible. */}
          </div>
        )}
      </main>

      {/* AI Generate Modal */}
      <AIGenerateModal
        isOpen={showAIGenerate}
        onClose={() => setShowAIGenerate(false)}
        mode="both"
        onAddToSlideshow={(item) => {
          setPhotos(prev => [...prev, {
            url: item.url,
            id: `ai_${Date.now()}`,
            type: item.type,
            duration: item.duration || globalDuration,
            fitMode: globalFitMode,
            videoDuration: item.duration,
            inPoint: 0,
            outPoint: item.duration || 0,
            position: { x: 50, y: 50 },
            zoom: 100,
            audioEnabled: item.type === 'video'  // AI clips: enable audio by default for videos
          }]);
          setShowAIGenerate(false);
        }}
      />

      {/* Manual Crop Modal — opens when user clicks the Edit-crop button on a
          photo thumbnail in Manual crop mode. Saves a focus point + zoom on
          the photo, applied via CSS object-position in preview and forwarded
          to HP Server via the slideshow payload's crop_positions array. */}
      <PhotoCropModal
        open={cropEditorPhotoIndex !== null && cropMode === 'manual'}
        imageUrl={photos[cropEditorPhotoIndex]?.url}
        initialPosition={photos[cropEditorPhotoIndex]?.cropPosition}
        initialZoom={photos[cropEditorPhotoIndex]?.cropZoom}
        aspect={cropAspect}
        onClose={() => setCropEditorPhotoIndex(null)}
        onSave={({ x, y, zoom }) => {
          setPhotos(prev => prev.map((p, i) => (
            i === cropEditorPhotoIndex
              ? { ...p, cropPosition: { x, y }, cropZoom: zoom }
              : p
          )));
          setCropEditorPhotoIndex(null);
        }}
      />

      {/* Video Format Drawer */}
      <VideoFormatDrawer
        open={showFormatDrawer}
        onOpenChange={setShowFormatDrawer}
        current={orientation}
        onSelect={setOrientation}
      />

      {/* Song Picker Drawer */}
      <SongPickerDrawer
        open={showSongDrawer}
        onOpenChange={setShowSongDrawer}
        songs={songs}
        currentSongId={selectedSong?.song_id}
        onSelect={(s) => setSelectedSong(s)}
      />
    </div>
  );
}
